import React, { useState, useRef, useEffect } from "react";
import { Select, Spin } from "antd";
import type { SelectProps } from "antd";

type OptionType = {
  label: string;
  value: string | number;
};

type LazySelectProps<T> = {
  value?: any;
  onChange?: (value: any) => void;
  fetchOptions: (params: {
    search?: string;
    page: number;
    pageSize: number;
  }) => Promise<T[]>;
  mapOption: (item: T) => OptionType;
  pageSize?: number;
  initialOptions?: OptionType[];
  mode?: SelectProps["mode"]; // "multiple" | "tags" | undefined
  onClear?: () => void;
};

export function LazySelect<T>({
  value,
  onChange,
  fetchOptions,
  mapOption,
  pageSize = 20,
  initialOptions,
  mode,
  onClear,
}: LazySelectProps<T>) {
  const [options, setOptions] = useState<OptionType[]>(initialOptions || []);
  const [page, setPage] = useState(1);
  const [fetching, setFetching] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const searchRef = useRef<string>("");
  const valueRef = useRef(value);
  const initialOptionsRef = useRef(initialOptions);
  const requestIdRef = useRef(0);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  useEffect(() => {
    if (initialOptions && initialOptions.length > 0) {
      setOptions((prev) => {
        const map = new Map(prev.map((o) => [o.value, o]));
        initialOptions.forEach((item) => {
          map.set(item.value, item);
        });
        return Array.from(map.values());
      });
      initialOptionsRef.current = initialOptions;
    }
  }, [initialOptions]);

  useEffect(() => {
    if (!value || value === valueRef.current) return;

    valueRef.current = value;

    const valuesToCheck = Array.isArray(value) ? value : [value];

    valuesToCheck.forEach((val) => {
      const exists = options.some((o) => o.value === val);
      if (!exists && initialOptionsRef.current) {
        const found = initialOptionsRef.current.find((o) => o.value === val);
        if (found) {
          setOptions((prev) => {
            const has = prev.some((o) => o.value === val);
            return has ? prev : [...prev, found];
          });
        }
      }
    });
  }, [value, options]);

  useEffect(
    () => () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    },
    [],
  );

  const loadOptions = async (search = "", nextPage = 1) => {
    const requestId = ++requestIdRef.current;
    setFetching(true);

    const data = await fetchOptions({
      search,
      page: nextPage,
      pageSize,
    });

    // Ignore out-of-order responses (a newer request has since started).
    if (requestId !== requestIdRef.current) return;

    const mapped = data.map(mapOption);

    setOptions((prev) => {
      const map = new Map<OptionType["value"], OptionType>();

      if (initialOptionsRef.current) {
        initialOptionsRef.current.forEach((item) => {
          map.set(item.value, item);
        });
      }

      // On a new search (page 1) drop the previously loaded list so stale,
      // non-matching options don't linger (filterOption is disabled). Keep the
      // options for currently selected values so the label still resolves.
      if (nextPage > 1) {
        prev.forEach((item) => {
          if (!initialOptionsRef.current?.some((io) => io.value === item.value)) {
            map.set(item.value, item);
          }
        });
      } else if (value != null) {
        const selected = Array.isArray(value) ? value : [value];
        prev.forEach((item) => {
          if (selected.includes(item.value)) {
            map.set(item.value, item);
          }
        });
      }

      mapped.forEach((item) => {
        map.set(item.value, item);
      });

      return Array.from(map.values());
    });

    setHasMore(data.length === pageSize);
    setHasLoaded(true);
    setFetching(false);
  };

  const handleSearch = (val: string) => {
    searchRef.current = val;
    setPage(1);
    setHasLoaded(false);

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      loadOptions(val, 1);
    }, 300);
  };

  // When a value is restored (e.g. from the URL) before options are fetched,
  // the Select has nothing to render the selected label with and shows blank.
  // Synthesize a fallback option (label = value) for any selected value that
  // isn't loaded yet; real options override it once they arrive.
  const displayOptions = React.useMemo(() => {
    if (value == null) return options;

    const selected = Array.isArray(value) ? value : [value];
    const missing = selected
      .filter((val) => val != null && !options.some((o) => o.value === val))
      .map((val) => ({ label: String(val), value: val }));

    return missing.length ? [...missing, ...options] : options;
  }, [value, options]);

  return (
    <Select
      mode={mode}
      value={value}
      onChange={onChange}
      options={displayOptions}
      allowClear
      onClear={onClear}
      placeholder="Select"
      notFoundContent={fetching ? <Spin size="small" /> : null}
      showSearch={{
        filterOption: false,
        onSearch: handleSearch,
      }}
      onFocus={() => {
        if (!hasLoaded) {
          loadOptions("", 1);
        }
      }}
      onPopupScroll={(e) => {
        const target = e.target as HTMLElement;

        if (
          target.scrollTop + target.offsetHeight >= target.scrollHeight - 10 &&
          hasMore &&
          !fetching
        ) {
          const nextPage = page + 1;
          setPage(nextPage);
          loadOptions(searchRef.current, nextPage);
        }
      }}
    />
  );
}
